import { useAuthentication } from '@/hooks/useAuthentication';
import { Button, forwardRef, type ButtonProps } from '@chakra-ui/react';
import useTranslation from 'next-translate/useTranslation';
import { Fragment } from 'react';
import { NotificationIndicator } from './NotificationIndicator';
import { ProfileIcon } from './ProfileIcon';
import { UserButtonContent } from './UserButtonContent';

interface Props extends ButtonProps {
  isOpen: boolean;
}

export const UserSessionButton = forwardRef<Props, 'button'>(({ isOpen, ...props }, ref) => {
  const { t } = useTranslation('common');
  const { isAuthenticating, isAuthenticated } = useAuthentication();

  const Wrapper = isAuthenticated ? NotificationIndicator : Fragment;

  return (
    <Wrapper>
      <Button
        ref={ref}
        data-testid="sign-in-button"
        variant="dark"
        p={{ base: 0, lg: 'space.4' }}
        h="unset"
        minW="unset"
        color="darkPurple"
        bg={{ lg: 'primary' }}
        rounded="full"
        isDisabled={isAuthenticating}
        cursor={isAuthenticating ? 'progress' : undefined}
        zIndex="header.behind"
        gap="space.8"
        aria-label={
          isAuthenticating
            ? t('header.connecting')
            : isAuthenticated
            ? isOpen
              ? t('header.hide-user-menu')
              : t('header.show-user-menu')
            : t('header.sign-in')
        }
        fontWeight="medium"
        _hover={{ bg: { lg: 'primaryLight' } }}
        _disabled={{ opacity: 1 }}
        {...props}
      >
        <ProfileIcon />
        <UserButtonContent />
      </Button>
    </Wrapper>
  );
});
